import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../utils/api'
import { formatRackName, rowIndexToLetter, rackLabel } from '../utils/rackName.logic'
import type { CalculationResponse } from '../types'

export { formatRackName, rowIndexToLetter, rackLabel }

export interface DcMeta {
  code: string;
  name: string;
  rack_naming_template?: string;
  prefix?: string;
  trunk_rack_index?: number;
  rows_count?: number;
  max_racks_per_row?: number;
  cable_trays_json?: unknown;
  aisle_zones_json?: unknown;
  floor_plan_json?: {
    originXMm: number;
    originYMm: number;
    widthMm: number;
    heightMm: number;
    tileSizeMm: number;
  };
}

export interface RackMeta {
  id: number;
  address_confirmed?: boolean;
  row_index: number;
  rack_index: number;
  label?: string;
  name?: string;
  x_m?: number;
  y_m?: number;
  width_m?: number;
  depth_m?: number;
  is_blocked?: boolean;
  u_slots?: number | null;
  heightU?: number;
  rotation?: number;
  tenant?: string;
  tenant_id?: string;
  status?: string;
  role?: string;
  type?: string;
  is_facility?: boolean;
  max_power_w?: number;
  max_weight_kg?: number;
}

export interface RoutePoint {
  x: number;
  y: number;
  z: number;
  description: string;
}

export interface CalcResult extends Partial<Omit<CalculationResponse, 'warning' | 'route' | 'total_length'>> {
  total_length: number;
  warning: string | null;
  route: RoutePoint[];
  shared_edge_ids?: string[];
}

export interface HistoryEntry {
  id: number;
  srcLabel: string;
  tgtLabel: string;
  cableType: string;
  length: number;
  warning: string | null;
  route: RoutePoint[];
  src: RackMeta;
  tgt: RackMeta;
  srcU: number;
  tgtU: number;
  result: CalcResult;
}

export const CABLE_LIMITS: Record<string, { label: string; maxM: number }> = {
  copper:   { label: 'Медь (RJ45 Cat.6A)',   maxM: 90 },
  dac:      { label: 'DAC (Twinax)',          maxM: 5 },
  mm_fiber: { label: 'Оптика (Многомод)',     maxM: 550 },
  sm_fiber: { label: 'Оптика (Одномод)',      maxM: 10000 },
}

export function useCalculatorLogic(dcCode?: string, currentDc?: DcMeta) {
  const [racks, setRacks] = useState<RackMeta[]>([])
  const [racksLoading, setRacksLoading] = useState(true)
  const [src, setSrc] = useState<RackMeta | null>(null)
  const [srcU, setSrcU] = useState<number>(42)
  const [tgt, setTgt] = useState<RackMeta | null>(null)
  const [tgtU, setTgtU] = useState<number>(42)
  const [cable, setCable] = useState('copper')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<CalcResult | null>(null)
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const [hid, setHid] = useState(0)
  const requestVersion = useRef(0)

  const invalidate = useCallback(() => {
    requestVersion.current += 1
    setResult(null)
    setError(null)
    setLoading(false)
  }, [])

  const changeSrc = useCallback((rack: RackMeta) => { invalidate(); setSrc(rack) }, [invalidate])
  const changeTgt = useCallback((rack: RackMeta) => { invalidate(); setTgt(rack) }, [invalidate])
  const changeSrcU = useCallback((u: number) => { invalidate(); setSrcU(u) }, [invalidate])
  const changeTgtU = useCallback((u: number) => { invalidate(); setTgtU(u) }, [invalidate])
  const changeCable = useCallback((value: string) => { invalidate(); setCable(value) }, [invalidate])
  const restoreHistory = useCallback((entry: HistoryEntry) => {
    invalidate()
    setSrc(entry.src); setTgt(entry.tgt)
    setSrcU(entry.srcU); setTgtU(entry.tgtU); setCable(entry.cableType)
    setResult(entry.result)
  }, [invalidate])

  useEffect(() => {
    if (!dcCode) return
    invalidate()
    setRacksLoading(true)
    setHistory([])
    setRacks([])
    let active = true
    setSrc(null); setTgt(null); setResult(null); setError(null)
    api.get(`/api/datacenters/${dcCode}/racks`)
      .then(r => {
        if (!active) return
        const sorted = (r.data as RackMeta[]).sort((a, b) =>
          a.row_index !== b.row_index ? a.row_index - b.row_index : a.rack_index - b.rack_index
        )
        setRacks(sorted)
        const validRacks = sorted.filter(r => r.row_index < 100)
        if (validRacks.length >= 2) { setSrc(validRacks[0]); setTgt(validRacks[validRacks.length - 1]) }
        else if (sorted.length >= 2) { setSrc(sorted[0]); setTgt(sorted[sorted.length - 1]) }
      })
      .catch(e => { if (active) setError(e.message) })
      .finally(() => { if (active) setRacksLoading(false) })
    return () => { active = false; requestVersion.current += 1 }
  }, [dcCode, invalidate])

  const handleSwap = useCallback(() => { 
    invalidate()
    setSrc(tgt); setTgt(src); 
    setSrcU(tgtU); setTgtU(srcU);
    setResult(null); 
  }, [src, tgt, srcU, tgtU, invalidate])

  const handleCalc = useCallback(async () => {
    if (!src || !tgt || !dcCode) return
    for (const [label, rack, unit] of [['A', src, srcU], ['B', tgt, tgtU]] as const) {
      const capacity = rack.is_facility ? 0 : rack.u_slots
      if (!Number.isInteger(unit) || unit < 1 || (capacity != null && unit > capacity)) {
        setError(capacity === 0 ? `Стойка ${label}: нет юнитов для подключения.`
          : `Юнит ${label}: укажите целое число от 1${capacity == null ? '' : ` до ${capacity}`}.`)
        return
      }
    }
    const version = ++requestVersion.current
    setLoading(true); setError(null); setResult(null)
    try {
      const res = await api.post('/api/calculator/calculate', {
        datacenter_code: dcCode, cable_type: cable,
        source_row: src.row_index, source_rack: src.rack_index,
        target_row: tgt.row_index, target_rack: tgt.rack_index,
        source_u_slot: srcU, target_u_slot: tgtU,
      })
      if (version !== requestVersion.current) return
      setResult(res.data)
      setHistory(h => [{
        id: hid, src, tgt,
        srcLabel: rackLabel(src, currentDc), tgtLabel: rackLabel(tgt, currentDc),
        cableType: cable, length: res.data.total_length,
        warning: res.data.warning, route: res.data.route,
        srcU, tgtU, result: res.data,
      }, ...h].slice(0, 10))
      setHid(x => x + 1)
    } catch (e: any) {
      if (version !== requestVersion.current) return
      const detail = e.response?.data?.detail
      setError(Array.isArray(detail) ? detail.map(item => item.msg || 'Неверный параметр').join('; ')
        : typeof detail === 'string' ? detail : e.message || 'Ошибка расчёта')
    } finally { if (version === requestVersion.current) setLoading(false) }
  }, [src, tgt, dcCode, cable, hid, currentDc, srcU, tgtU])

  const handleRackClick = useCallback((rack: RackMeta) => {
    invalidate()
    if (!src) { setSrc(rack); return }
    if (!tgt) { setTgt(rack); return }
    setSrc(rack); setTgt(null); setResult(null)
  }, [src, tgt, invalidate])

  return {
    racks, racksLoading, src, setSrc: changeSrc, srcU, setSrcU: changeSrcU,
    tgt, setTgt: changeTgt, tgtU, setTgtU: changeTgtU,
    cable, setCable: changeCable, loading, error, setError, result, setResult,
    history, setHistory, restoreHistory, handleSwap, handleCalc, handleRackClick
  }
}
